import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, CalendarHeart, Loader2, CheckCircle2 } from "lucide-react";
import { CENTER } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  parent_name: "",
  email: "",
  phone: "",
  child_age: "",
  preferred_date: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.parent_name || !form.email || !form.phone || !form.child_age) {
      toast.error("Please fill in your name, email, phone, and child's age.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/tour-requests`, form);
      setSubmitted(true);
      toast.success("Tour request sent! We'll reach out to you shortly. 💛");
      setForm(initialForm);
    } catch (err) {
      toast.error("Something went wrong. Please call us or try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-sky-400 transition";

  return (
    <section id="contact" className="py-20 md:py-28 px-5 md:px-10 bg-white">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-16">
        {/* Contact info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-sm font-semibold tracking-wider uppercase text-sky-500">
            Visit Us
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
            Come see the magic in person
          </h2>
          <p className="mt-4 text-lg text-slate-600 leading-relaxed">
            We'd love to show you around! Schedule a tour or reach out anytime — we're
            here to answer your questions and help your family feel at home.
          </p>

          <div className="mt-8 space-y-4">
            <a
              data-testid="contact-address"
              href="https://maps.google.com/?q=8748+Balboa+Drive+Cincinnati+Ohio+45231"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-4 bg-[#FDFAF5] rounded-2xl p-5 border border-slate-100 hover:shadow-md transition group"
            >
              <span className="flex items-center justify-center w-12 h-12 rounded-xl bg-sky-50 text-sky-500 shrink-0 group-hover:scale-110 transition-transform">
                <MapPin className="w-6 h-6" />
              </span>
              <div>
                <p className="font-bold text-slate-900">Our Location</p>
                <p className="text-slate-600">{CENTER.address}</p>
              </div>
            </a>

            <div className="flex items-start gap-4 bg-[#FDFAF5] rounded-2xl p-5 border border-slate-100">
              <span className="flex items-center justify-center w-12 h-12 rounded-xl bg-rose-50 text-rose-500 shrink-0">
                <Phone className="w-6 h-6" />
              </span>
              <div>
                <p className="font-bold text-slate-900">Call Us</p>
                <div className="flex flex-col">
                  {CENTER.phones.map((p) => (
                    <a
                      key={p}
                      data-testid={`contact-phone-${p}`}
                      href={`tel:${p.replace(/-/g, "")}`}
                      className="text-slate-600 hover:text-sky-500 transition-colors"
                    >
                      {p}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <a
              data-testid="contact-email-link"
              href={`mailto:${CENTER.email}`}
              className="flex items-start gap-4 bg-[#FDFAF5] rounded-2xl p-5 border border-slate-100 hover:shadow-md transition group"
            >
              <span className="flex items-center justify-center w-12 h-12 rounded-xl bg-amber-50 text-amber-500 shrink-0 group-hover:scale-110 transition-transform">
                <Mail className="w-6 h-6" />
              </span>
              <div>
                <p className="font-bold text-slate-900">Email Us</p>
                <p className="text-slate-600 break-all">{CENTER.email}</p>
              </div>
            </a>
          </div>
        </motion.div>

        {/* Tour form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="bg-[#FDFAF5] rounded-3xl p-7 md:p-9 border border-slate-100 shadow-sm"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="flex items-center justify-center w-12 h-12 rounded-2xl bg-sky-400 text-white shadow-[0_4px_14px_0_rgba(56,189,248,0.39)]">
              <CalendarHeart className="w-6 h-6" />
            </span>
            <div>
              <h3 className="text-xl font-bold text-slate-900">Schedule a Tour</h3>
              <p className="text-sm text-slate-500">We'll confirm your visit soon</p>
            </div>
          </div>

          {submitted ? (
            <div
              data-testid="tour-success-message"
              className="flex flex-col items-center text-center py-10"
            >
              <CheckCircle2 className="w-16 h-16 text-emerald-400" />
              <h4 className="mt-4 text-xl font-bold text-slate-900">Thank you!</h4>
              <p className="mt-2 text-slate-600">
                Your tour request has been received. We'll be in touch very soon.
              </p>
              <button
                data-testid="tour-another-request"
                onClick={() => setSubmitted(false)}
                className="mt-6 text-sky-500 font-semibold hover:underline"
              >
                Submit another request
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="tour-form">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                  Parent / Guardian Name *
                </label>
                <input
                  data-testid="tour-name-input"
                  type="text"
                  value={form.parent_name}
                  onChange={update("parent_name")}
                  placeholder="Your full name"
                  className={inputCls}
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                    Email *
                  </label>
                  <input
                    data-testid="tour-email-input"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@email.com"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                    Phone *
                  </label>
                  <input
                    data-testid="tour-phone-input"
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="(513) 000-0000"
                    className={inputCls}
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                    Child's Age *
                  </label>
                  <input
                    data-testid="tour-child-age-input"
                    type="text"
                    value={form.child_age}
                    onChange={update("child_age")}
                    placeholder="e.g. 2 years"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                    Preferred Date
                  </label>
                  <input
                    data-testid="tour-date-input"
                    type="date"
                    value={form.preferred_date}
                    onChange={update("preferred_date")}
                    className={inputCls}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                  Message
                </label>
                <textarea
                  data-testid="tour-message-input"
                  value={form.message}
                  onChange={update("message")}
                  rows={4}
                  placeholder="Tell us a little about your family or any questions you have..."
                  className={`${inputCls} resize-none`}
                />
              </div>

              <button
                data-testid="tour-submit-button"
                type="submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 bg-sky-400 hover:bg-sky-500 text-white font-bold py-3.5 px-8 rounded-full shadow-[0_4px_14px_0_rgba(56,189,248,0.39)] hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-60 disabled:hover:translate-y-0"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" /> Sending...
                  </>
                ) : (
                  "Request My Tour"
                )}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
